/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * Definition of the product ordered and managed in the logistic chain. The product is defined and owned by the seller of the goods. TODO: add dangerous goods attributes.
 * @export
 * @interface Product
 */
export interface Product {
    /**
     * Free text name of the product given by the seller.
     * @type {string}
     * @memberof Product
     */
    commodityName?: string;
    /**
     * International product classification code for custom declarations.
     *                TODO search ontology for HS Codes
     * @type {string}
     * @memberof Product
     */
    hsCode?: string;
    /**
     * International product description for customs declarations.
     * @type {string}
     * @memberof Product
     */
    hsCommodityDescription?: string;
    /**
     * International product name for customs declarations.
     * @type {string}
     * @memberof Product
     */
    hsCommodityName?: string;
    /**
     * International product type for customs declarations.
     *                TODO search ontology for HS Codes
     * @type {string}
     * @memberof Product
     */
    hsType?: string;
    /**
     * Description of the product given by the seller.
     * @type {string}
     * @memberof Product
     */
    productDescription?: string;
    /**
     * Unique product identifier for the seller.
     * @type {string}
     * @memberof Product
     */
    productIdentifier?: string;
    /**
     * List of contacts related to the product. This can include individual or teams to be contacted about the product.
     * @type {Array<Contact>}
     * @memberof Product
     */
    contacts?: Array<Contact>;
    /**
     * List of documents related to the product (e.g., instructions, photos, safety guidelines).
     * @type {Array<Document>}
     * @memberof Product
     */
    documents?: Array<Document>;
    /**
     * External references of the product should be used when they are defined in a legacy system registering the product under another reference identifier than the URI. This is useful to search for the product using the ID from the legacy app. Frequent ExternalReference Types:
     *                            - 'SELLER_ID'
     *                            - 'BUYER_ID'
     *                            - 'PROVIDER_ID' 
     * @type {Array<ExternalReference>}
     * @memberof Product
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * Special characteristics applied to the product and to be shared with a provider.
     * @type {Array<SpecialCharacteristic>}
     * @memberof Product
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * Special instructions applied to the product and to be shared with a provider.
     * @type {Array<SpecialInstruction>}
     * @memberof Product
     */
    specialInstructions?: Array<SpecialInstruction>;
}

/**
 * Check if a given object implements the Product interface.
 */
export function instanceOfProduct(value: object): value is Product {
    return true;
}

export function ProductFromJSON(json: any): Product {
    return ProductFromJSONTyped(json, false);
}

export function ProductFromJSONTyped(json: any, ignoreDiscriminator: boolean): Product {
    if (json == null) {
        return json;
    }
    return {
        
        'commodityName': json['commodityName'] == null ? undefined : json['commodityName'],
        'hsCode': json['hsCode'] == null ? undefined : json['hsCode'],
        'hsCommodityDescription': json['hsCommodityDescription'] == null ? undefined : json['hsCommodityDescription'],
        'hsCommodityName': json['hsCommodityName'] == null ? undefined : json['hsCommodityName'],
        'hsType': json['hsType'] == null ? undefined : json['hsType'],
        'productDescription': json['productDescription'] == null ? undefined : json['productDescription'],
        'productIdentifier': json['productIdentifier'] == null ? undefined : json['productIdentifier'],
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
    };
}

export function ProductToJSON(json: any): Product {
    return ProductToJSONTyped(json, false);
}

export function ProductToJSONTyped(value?: Product | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'commodityName': value['commodityName'],
        'hsCode': value['hsCode'],
        'hsCommodityDescription': value['hsCommodityDescription'],
        'hsCommodityName': value['hsCommodityName'],
        'hsType': value['hsType'],
        'productDescription': value['productDescription'],
        'productIdentifier': value['productIdentifier'],
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
    };
}

